import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../components/common/DataTable";
import PageHeader from "../components/common/PageHeader";
import LocationMapView from "../components/common/LocationMapView";
import GooglePlacesTopSpreadsheetView from "../components/GooglePlacesTopSpreadsheetView";
import LocationHistoryModal from "../components/LocationHistoryModal";
import api from "../services/api";
import { showToast, confirmDelete } from "../helper/notifications";
import { useDebounce } from "../hook/useDebounce";
import { Icon } from "@iconify/react";

const LocationsGooglePage = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // View Mode: 'table', 'sheet', or 'map'
  const [viewMode, setViewMode] = useState("table");

  // Version History Modal State
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyPlace, setHistoryPlace] = useState(null);

  // Syncing State
  const [syncing, setSyncing] = useState(false);
  const [syncingPlaceId, setSyncingPlaceId] = useState(null);

  // Sorting State
  const [sortConfig, setSortConfig] = useState({ key: "last_updated", direction: "desc" });
  const debouncedSearch = useDebounce(search, 500);

  // 1. Fetch Data from google_places_full API
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const isFullView = viewMode === "map" || viewMode === "sheet";
      const res = await api.get("/google-places", {
        params: {
          search: debouncedSearch,
          page: isFullView ? 1 : page,
          limit: isFullView ? 250 : limit,
          sort_by: sortConfig.key,
          order: sortConfig.direction,
        },
      });
      setData(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (error) {
      showToast("error", "Failed to load Google Places records");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, limit, sortConfig, viewMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 2. Handle Action & Goal Updates (PATCH)
  const handleUpdateStatus = async (placeId, field, value) => {
    try {
      await api.patch(`/google-places/${placeId}`, { [field]: value });
      showToast("success", `${field.replace("_", " ")} updated`);
      setData((prev) =>
        prev.map((item) => (item.place_id === placeId ? { ...item, [field]: value } : item))
      );
    } catch (error) {
      showToast("error", "Update failed");
    }
  };

  // 3. Sync from Google Places API (Batch & Single)
  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/google-places/sync-from-google", { limit: 250 });
      if (res.data?.success) {
        showToast("success", res.data.message || "Successfully synced places with Google Places API.");
        fetchData();
      } else {
        showToast("error", res.data?.error || "Sync failed");
      }
    } catch (error) {
      showToast("error", error.response?.data?.error || "Failed to sync with Google Places API");
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncSingle = useCallback(async (row) => {
    if (!row?.place_id) return;
    setSyncingPlaceId(row.place_id);
    try {
      const res = await api.post(`/google-places/sync-single/${row.place_id}`);
      if (res.data?.success) {
        showToast("success", res.data.message || `Successfully synced ${row.name || "place"}`);
        fetchData();
      } else {
        showToast("error", res.data?.error || "Sync failed");
      }
    } catch (error) {
      showToast("error", error.response?.data?.error || "Failed to sync location from Google");
    } finally {
      setSyncingPlaceId(null);
    }
  }, [fetchData]);

  // 4. Delete Logic
  const handleDelete = useCallback((row) => {
    confirmDelete(row.name || "this place", async () => {
      try {
        await api.delete(`/google-places/${row.place_id}`);
        showToast("success", "Deleted successfully");
        fetchData();
      } catch (error) {
        showToast("error", "Delete failed");
      }
    });
  }, [fetchData]);

  // 4. Columns Configuration for DataTable with Customizer
  const columns = useMemo(() => [
    {
      key: "name",
      label: "Name",
      category: "General",
      defaultVisible: true,
      className: "text-xs fw-semibold",
      sortable: true,
      render: (row) => (
        <span
          className="text-primary-600 pointer hover-text-decoration-underline"
          onClick={() => navigate(`/google-places/${row.place_id}/edit`)}
        >
          {row.name || "Unnamed Place"}
        </span>
      ),
    },
    {
      key: "business_status",
      label: "Business Status",
      category: "General",
      defaultVisible: true,
      sortable: true,
      render: (row) => {
        const isOperational = row.business_status === "OPERATIONAL";
        return (
          <span
            className={`badge px-8 py-2 radius-4 fw-medium text-xs ${
              isOperational
                ? "bg-success-focus text-success-main"
                : "bg-warning-focus text-warning-main"
            }`}
          >
            {row.business_status || "UNKNOWN"}
          </span>
        );
      },
    },
    {
      key: "types",
      label: "Category",
      category: "General",
      defaultVisible: true,
      className: "text-xs text-secondary-light fw-medium",
      render: (row) => {
        let arr = [];
        if (Array.isArray(row.types)) arr = row.types;
        else if (typeof row.types === "string") {
          try { arr = JSON.parse(row.types); } catch (e) { arr = [row.types]; }
        }
        if (row.business_category) arr = [row.business_category, ...arr];
        return arr.length > 0 ? (
          <span className="badge bg-neutral-100 text-neutral-700 px-8 py-2 text-xs radius-4">
            {arr[0].replace(/_/g, " ")}
          </span>
        ) : "—";
      },
    },
    {
      key: "action_status",
      label: "Action",
      category: "Action / Goal",
      defaultVisible: true,
      sortable: true,
      render: (row) => (
        <select
          className="form-select form-select-sm"
          style={{ width: "118px" }}
          value={row.action_status || ""}
          onChange={(e) => handleUpdateStatus(row.place_id, "action_status", e.target.value)}
        >
          <option value="">Select Action</option>
          <option value="Claim">Claim</option>
          <option value="Ignore">Ignore</option>
        </select>
      ),
    },
    {
      key: "goal",
      label: "Goal",
      category: "Action / Goal",
      defaultVisible: true,
      sortable: true,
      render: (row) => (
        <select
          className="form-select form-select-sm"
          style={{ width: "118px" }}
          value={row.goal || ""}
          onChange={(e) => handleUpdateStatus(row.place_id, "goal", e.target.value)}
        >
          <option value="">Select Goal</option>
          <option value="Own">Own</option>
          <option value="Deactivate">Deactivate</option>
          <option value="Move Out">Move out</option>
        </select>
      ),
    },
    {
      key: "formatted_address",
      label: "Address",
      category: "Address",
      defaultVisible: true,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: "220px" }} title={row.formatted_address}>
          {row.formatted_address || "—"}
        </span>
      ),
    },
    {
      key: "rating",
      label: "Rating",
      category: "Reputation",
      defaultVisible: true,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => (
        row.rating ? (
          <span className="d-flex align-items-center gap-1">
            <Icon icon="solar:star-bold" className="text-warning-main text-sm" />
            <span className="fw-semibold text-xs">{row.rating}</span>
            <span className="text-2xs text-secondary-light">({row.user_ratings_total || 0})</span>
          </span>
        ) : "—"
      ),
    },
    {
      key: "user_ratings_total",
      label: "Reviews Count",
      category: "Reputation",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.user_ratings_total ?? "—",
    },
    {
      key: "phone_number",
      label: "Phone",
      category: "Contact",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.phone_number || "—",
    },
    {
      key: "website_uri",
      label: "Website",
      category: "Contact",
      defaultVisible: false,
      className: "text-xs text-secondary-light fw-medium",
      render: (row) => (
        row.website_uri ? (
          <a
            href={row.website_uri}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 text-decoration-none d-flex align-items-center gap-1 hover-text-decoration-underline"
          >
            <span className="text-truncate" style={{ maxWidth: "160px" }}>
              {row.website_uri}
            </span>
            <Icon icon="solar:arrow-right-up-linear" className="text-2xs" />
          </a>
        ) : "—"
      ),
    },
    {
      key: "place_id",
      label: "Place ID",
      category: "System",
      defaultVisible: false,
      className: "text-xs text-secondary-light font-monospace",
      render: (row) => row.place_id || "—",
    },
    {
      key: "last_updated",
      label: "Last Updated",
      category: "System",
      defaultVisible: true,
      className: "text-xs text-secondary-light fw-medium",
      sortable: true,
      render: (row) => (
        row.last_updated
          ? new Date(row.last_updated).toLocaleString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "—"
      ),
    },
  ], [navigate]);

  // 5. Actions per row in DataTable
  const tableActions = useMemo(() => [
    {
      icon: "solar:refresh-square-linear",
      title: "Sync from Google Places API",
      className: "w-28-px h-28-px bg-primary-50 text-primary-600 rounded d-flex align-items-center justify-content-center border-0 text-xs",
      onClick: handleSyncSingle,
    },
    {
      icon: "solar:history-linear",
      title: "View History",
      className: "w-28-px h-28-px bg-info-focus text-info-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
      onClick: (row) => {
        setHistoryPlace(row);
        setHistoryModalOpen(true);
      },
    },
    {
      icon: "ri-edit-line",
      title: "Edit Location Details",
      className: "w-28-px h-28-px bg-success-focus text-success-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
      onClick: (row) => navigate(`/google-places/${row.place_id}/edit`),
    },
    {
      icon: "ri-delete-bin-line",
      title: "Delete Location",
      className: "w-28-px h-28-px bg-danger-focus text-danger-main rounded d-flex align-items-center justify-content-center border-0 text-xs",
      onClick: handleDelete,
    },
  ], [navigate, handleDelete, handleSyncSingle]);

  // Map Data items normalization
  const mapLocations = useMemo(() => {
    return data.map((item) => ({
      ...item,
      id: item.place_id,
      name: item.name,
      latitude: item.location_lat,
      longitude: item.location_lng,
      address: item.formatted_address,
    }));
  }, [data]);

  return (
    <>
      <PageHeader
        title="Google Places"
        subtitle="Manage imported Google Places and locations."
        actions={
          <>
            <button
              type="button"
              className="btn btn-primary btn-sm d-flex align-items-center gap-2 radius-8 px-14 py-8 shadow-sm"
              onClick={handleSyncAll}
              disabled={syncing || loading}
              title="Query Google Places API for all records and record new version snapshots in history"
            >
              <Icon
                icon={syncing ? "line-md:loading-loop" : "ri:google-fill"}
                className="text-base"
              />
              <span>{syncing ? "Syncing with Google..." : "Sync with Google"}</span>
            </button>

            <div className="btn-group radius-8 overflow-hidden border">
              <button
                type="button"
                className={`btn btn-sm ${
                  viewMode === "table" ? "btn-primary" : "btn-light text-neutral-600"
                }`}
                onClick={() => setViewMode("table")}
              >
                <Icon icon="ri:table-line" className="me-1 text-base" /> Table
              </button>
              <button
                type="button"
                className={`btn btn-sm ${
                  viewMode === "sheet" ? "btn-primary" : "btn-light text-neutral-600"
                }`}
                onClick={() => setViewMode("sheet")}
              >
                <Icon icon="ri:file-excel-2-line" className="me-1 text-base" /> Sheet View
              </button>
              <button
                type="button"
                className={`btn btn-sm ${
                  viewMode === "map" ? "btn-primary" : "btn-light text-neutral-600"
                }`}
                onClick={() => setViewMode("map")}
              >
                <Icon icon="ri:map-pin-line" className="me-1 text-base" /> Map View
              </button>
            </div>
          </>
        }
      />

      {/* 1. Table View */}
      {viewMode === "table" && (
        <DataTable
          data={data}
          loading={loading}
          searchTerm={search}
          onSearchChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          showCheckbox={false}
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => {
            setLimit(v);
            setPage(1);
          }}
          columns={columns}
          actions={tableActions}
          sortConfig={sortConfig}
          onSortChange={setSortConfig}
          columnStorageKey="loqation_table_columns_top_google_places_v1"
        />
      )}

      {/* 2. Sheet View */}
      {viewMode === "sheet" && (
        <GooglePlacesTopSpreadsheetView
          data={data}
          loading={loading}
          syncing={syncing}
          syncingPlaceId={syncingPlaceId}
          onSyncAll={handleSyncAll}
          onSyncSingle={handleSyncSingle}
          onRefresh={fetchData}
          onOpenHistory={(row) => {
            setHistoryPlace(row);
            setHistoryModalOpen(true);
          }}
          onDelete={handleDelete}
        />
      )}

      {/* 3. Map View */}
      {viewMode === "map" && (
        <LocationMapView
          locations={mapLocations}
          loading={loading}
          getViewRoute={(item) => `/google-places/${item.place_id || item.id}/edit`}
        />
      )}

      {/* Version History Modal */}
      {historyModalOpen && historyPlace && (
        <LocationHistoryModal
          endpoint={`/google-places/${historyPlace.place_id}/history`}
          onClose={() => {
            setHistoryModalOpen(false);
            setHistoryPlace(null);
            fetchData();
          }}
        />
      )}
    </>
  );
};

export default LocationsGooglePage;